/**
 * StakeSelectionScreen.tsx
 * Stake selection screen for matchmaking
 */

import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  ButtonDropdown,
  Icon,
  Pill,
  TextBlock,
  TIconImages,
  useModal,
} from '@skillarea/lib-frontend';
import { Title } from '@skillarea/lib-frontend';
import {
  EFreePlayDifficulty,
  formatMatchPayoutLabel,
  formatMoney,
  GameReturnDto,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import styles from '../MatchmakingModal.module.scss';
import {
  MAX_STAKE_CENTS,
  MIN_STAKE_CENTS,
  resolveStakeSelectionInitialStake,
  STAKE_OPTION_VALUES_CENTS,
} from '../utils/resolveStakeSelectionInitialStake';

import { MetadataPillSeparator } from './MetadataPillSeparator';

import { useLocalizedGame } from '~/hooks/useLocalizedGame';
import { InsufficientBalanceModal } from '~/components/InsufficientBalanceModal/InsufficientBalanceModal';
import {
  isMatchmakingInsufficientBalanceError,
  MATCHMAKING_INSUFFICIENT_BALANCE_ERROR,
} from '~/lib/matchmakingLocale';
import { useIsMaintenanceEnabled } from '~/stores/maintenanceStore';
import { useIsCurrentRouteDraining } from '~/stores/overlapStore';

export interface IGameMetadata {
  category?: string;
  playerCount?: number;
}

/** All amounts are in cents (same as balance in the API and formatMoney in lib-shared). */
export interface IStakeSelectionScreenProps {
  externalError?: string | null;
  game: GameReturnDto;
  gameMetadata?: IGameMetadata;
  /** Default stake, in cents */
  initialStake?: number;
  insufficientBalanceModalNonce?: number;
  /** User balance, in cents */
  userBalance: number;
  /** User stake limit in cents (by league) */
  stakeCapCents: number;
  /** Wins needed for the next league (if not at the top) */
  nextLeagueWins?: number | null;
  /** stake in cents */
  onFindOpponent: (stake: number) => void;
  onFreePlay?: (difficulty: EFreePlayDifficulty) => void;
  onInviteFriend?: (gameId: number) => void;
}

function isFreePlayDifficulty(value: string): value is EFreePlayDifficulty {
  return (
    value === EFreePlayDifficulty.easy ||
    value === EFreePlayDifficulty.medium ||
    value === EFreePlayDifficulty.hard
  );
}

export const StakeSelectionScreen: FC<IStakeSelectionScreenProps> = ({
  externalError,
  game,
  gameMetadata,
  initialStake,
  insufficientBalanceModalNonce = 0,
  nextLeagueWins,
  onFindOpponent,
  onFreePlay,
  stakeCapCents,
  userBalance,
}) => {
  const { i18n, t: tGames } = useTranslation('games');
  const { t } = useTranslation('matchmaking');
  const localizedGame = useLocalizedGame(game);
  const navigate = useNavigate();
  const { closeModal, openModal } = useModal();
  const isMaintenanceEnabled = useIsMaintenanceEnabled();
  const isCurrentRouteDraining = useIsCurrentRouteDraining();
  const isMatchmakingStartDisabled =
    isMaintenanceEnabled || isCurrentRouteDraining;
  const maxStakeDollars = Math.floor(stakeCapCents / 100);
  const maxAllowedStakeCents = Math.min(stakeCapCents, MAX_STAKE_CENTS);
  const availableStakeOptions = useMemo(
    () =>
      STAKE_OPTION_VALUES_CENTS.map((value) => ({
        value: String(value),
        label: formatMoney(value),
        secondaryLabel: t('stake.optionWin', {
          amount: formatMatchPayoutLabel(value),
        }),
        disabled: value > maxAllowedStakeCents,
      })),
    [maxAllowedStakeCents, t],
  );
  const hasAffordableStake = availableStakeOptions.some(
    (opt) => !opt.disabled && Number(opt.value) <= userBalance,
  );

  const resolvedInitialStake = String(
    resolveStakeSelectionInitialStake({
      initialStake,
      stakeCapCents,
      userBalance,
    }),
  );

  const [stake, setStake] = useState<string>(resolvedInitialStake);
  const selectedStakeCents = parseInt(stake, 10);
  const selectedStakeLabel = formatMoney(selectedStakeCents);
  const [freePlayDifficulty, setFreePlayDifficulty] =
    useState<EFreePlayDifficulty>(EFreePlayDifficulty.easy);
  const [error, setError] = useState<string | null>(null);
  const freePlayDifficultyOptions = [
    { value: EFreePlayDifficulty.easy, label: t('stake.difficulty.easy') },
    { value: EFreePlayDifficulty.medium, label: t('stake.difficulty.medium') },
    { value: EFreePlayDifficulty.hard, label: t('stake.difficulty.hard') },
  ] as const;
  const effectiveError =
    error ??
    externalError ??
    (!hasAffordableStake ? MATCHMAKING_INSUFFICIENT_BALANCE_ERROR : null);
  const hasInsufficientBalance =
    isMatchmakingInsufficientBalanceError(effectiveError);
  const visibleError = hasInsufficientBalance ? null : effectiveError;

  const openInsufficientBalanceModal = useCallback(() => {
    openModal(
      <InsufficientBalanceModal
        isOpen
        onClose={closeModal}
        onGoToBalance={() => {
          closeModal();
          void navigate({
            to: '/account/balance/deposit',
          });
        }}
      />,
      {},
      true,
    );
  }, [closeModal, navigate, openModal]);

  useEffect(() => {
    if (
      !isMatchmakingInsufficientBalanceError(externalError) ||
      insufficientBalanceModalNonce <= 0
    ) {
      return;
    }

    setError(null);
    openInsufficientBalanceModal();
  }, [
    externalError,
    insufficientBalanceModalNonce,
    openInsufficientBalanceModal,
  ]);

  const handleStakeChange = (value: string) => {
    setStake(value);
    setError(null);
  };

  const handleFindOpponent = () => {
    const stakeNumber = parseInt(stake, 10);
    // Validation
    if (stakeNumber < MIN_STAKE_CENTS || stakeNumber > maxAllowedStakeCents) {
      setError(
        t('stake.entryFeeRange', {
          min: formatMoney(MIN_STAKE_CENTS),
          max: formatMoney(maxAllowedStakeCents),
        }),
      );
      return;
    }

    if (stakeNumber > userBalance) {
      setError(null);
      openInsufficientBalanceModal();
      return;
    }

    setError(null);
    onFindOpponent(stakeNumber);
  };

  const handleFreePlay = () => {
    setError(null);
    onFreePlay?.(freePlayDifficulty);
  };

  return (
    <div className={styles.screen} data-testid="stake-selection-screen">
      {/* Header with game image and name */}
      <div className={styles.header}>
        <Title
          className={styles.screenTitle}
          color="contrast"
          size="l"
          variant="benzin"
        >
          {localizedGame.name}
        </Title>

        <div className={styles.metadataPills}>
          {gameMetadata?.category && (
            <>
              <Pill icon={gameMetadata.category as TIconImages} size="m">
                {gameMetadata.category}
              </Pill>
              <MetadataPillSeparator />
            </>
          )}
          {gameMetadata?.playerCount && (
            <>
              <Pill icon="players" size="m">
                {t('stake.players', {
                  count: gameMetadata.playerCount,
                })}
              </Pill>
              <MetadataPillSeparator />
            </>
          )}
          <Pill icon="coins" size="m">
            {tGames('ui.maxEntryFee', {
              amount: maxStakeDollars.toLocaleString(i18n.language),
            })}
          </Pill>
        </div>

        {game.image && (
          <div className={styles.gameImageContainer}>
            <img
              alt={localizedGame.name}
              className={styles.gameImage}
              src={game.image}
            />
            <div className={styles.overlay}></div>
          </div>
        )}
      </div>

      <div className={styles.content}>
        {visibleError && (
          <div className={styles.errorBlock}>
            <div className={styles.errorText}>
              {isMatchmakingInsufficientBalanceError(visibleError)
                ? t('stake.insufficientBalance')
                : visibleError}
            </div>
          </div>
        )}
        {nextLeagueWins != null && nextLeagueWins > 0 && (
          <TextBlock className={styles.nextLeagueText} color="noteContrast">
            {t('stake.nextLeague', { count: nextLeagueWins })}
          </TextBlock>
        )}
      </div>

      <div className={styles.stakeAndButton}>
        <ButtonDropdown
          className={styles.findOpponentButton}
          color="lavender"
          contentClassName={styles.stakeDropdownMenu}
          iconRight={<Icon img="arrow-pixel" size="xs" type="colored" />}
          items={availableStakeOptions}
          label={selectedStakeLabel}
          onChange={handleStakeChange}
          size="s"
          value={stake}
          variant="outline"
        />

        <Button
          className={styles.findOpponentButton}
          color="accent"
          data-testid="matchmaking-find-game-button"
          disabled={isMatchmakingStartDisabled}
          name={t('stake.findOpponent')}
          onClick={handleFindOpponent}
          size="s"
          variant="filled"
        >
          {t('stake.findOpponent')}
        </Button>
      </div>

      <div aria-hidden="true" className={styles.freePlayOr}>
        {t('stake.freePlayOr')}
      </div>

      <div className={styles.stakeAndButton}>
        <ButtonDropdown
          className={styles.findOpponentButton}
          color="lavender"
          iconRight={<Icon img="arrow-pixel" size="xs" type="colored" />}
          items={freePlayDifficultyOptions.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
          onChange={(value) => {
            if (isFreePlayDifficulty(value)) {
              setFreePlayDifficulty(value);
            }
            setError(null);
          }}
          size="s"
          value={freePlayDifficulty}
          variant="outline"
        />

        <Button
          className={styles.findOpponentButton}
          color="primary"
          data-testid="matchmaking-free-play-button"
          disabled={isMatchmakingStartDisabled || !onFreePlay}
          name={t('stake.freePlay')}
          onClick={handleFreePlay}
          size="s"
          variant="filled"
        >
          {t('stake.freePlay')}
        </Button>
      </div>
    </div>
  );
};
